import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import api, { ApiError } from '../../lib/apiClient';

// TS port of the source (Falah) app's pages/system/Templates.jsx.
//
// Keep in sync with MessageTemplate::DEFAULTS on the backend
// (api/app/Models/MessageTemplate.php) — each key here needs a matching
// entry there (GET/PUT /templates/{key} 404s otherwise), and each new
// approval-notification email added there needs a matching entry here.
const TEMPLATES: { key: string; title: string }[] = [
  { key: 'leave_request_submitted', title: 'Leave Request Submitted' },
  { key: 'leave_request_approved', title: 'Leave Request Approved' },
  { key: 'leave_request_rejected', title: 'Leave Request Rejected' },
  { key: 'overtime_request_submitted', title: 'Overtime Request Submitted' },
  { key: 'overtime_request_approved', title: 'Overtime Request Approved' },
  { key: 'overtime_request_rejected', title: 'Overtime Request Rejected' },
  { key: 'performance_review_submitted', title: 'Performance Review Submitted' },
  { key: 'performance_review_approved', title: 'Performance Review Approved' },
  { key: 'performance_review_rejected', title: 'Performance Review Rejected' },
  { key: 'timesheet_entry_submitted', title: 'Timesheet Entry Submitted' },
  { key: 'timesheet_entry_approved', title: 'Timesheet Entry Approved' },
  { key: 'timesheet_entry_rejected', title: 'Timesheet Entry Rejected' },
];

const PLACEHOLDER_HINT = 'Placeholders are documented per-template on the backend in MessageTemplate::DEFAULTS.';

interface TemplateForm {
  subject: string;
  body: string;
}

function TemplateSection({ templateKey, title }: { templateKey: string; title: string }) {
  const [form, setForm] = useState<TemplateForm | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [errors, setErrors] = useState<Record<string, string[]>>({});

  useEffect(() => {
    api.get<TemplateForm>(`/templates/${templateKey}`).then((res) => {
      setForm({ subject: res.data.subject, body: res.data.body });
      setSaved(false);
      setErrors({});
    });
  }, [templateKey]);

  const set = (key: keyof TemplateForm) => (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setSaved(false);
    if (!form) return;
    setForm({ ...form, [key]: e.target.value });
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (!form) return;
    setSaving(true);
    setErrors({});
    try {
      const res = await api.put<TemplateForm>(`/templates/${templateKey}`, form);
      setForm({ subject: res.data.subject, body: res.data.body });
      setSaved(true);
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    } finally {
      setSaving(false);
    }
  };

  if (!form) return null;

  return (
    <div className="card">
      <h2>{title}</h2>
      <form onSubmit={save} className="form-grid">
        {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
        {saved && <div className="alert alert-success span-2">Template saved.</div>}
        <label className="span-2">
          Subject
          <input value={form.subject} onChange={set('subject')} required maxLength={255} />
          {errors.subject && <small className="error">{errors.subject[0]}</small>}
        </label>
        <label className="span-2">
          Message
          <textarea rows={8} value={form.body} onChange={set('body')} required />
          {errors.body && <small className="error">{errors.body[0]}</small>}
        </label>
        <small className="muted span-2">{PLACEHOLDER_HINT}</small>
        <div className="form-actions span-2">
          <button className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  );
}

export function Templates() {
  const [selectedKey, setSelectedKey] = useState(TEMPLATES[0]?.key ?? '');
  const selected = TEMPLATES.find((t) => t.key === selectedKey);

  return (
    <>
      <header className="page-header">
        <h1>Templates</h1>
      </header>
      <p className="muted">Wording used in system-generated emails.</p>

      {TEMPLATES.length === 0 ? (
        <div className="card">
          <p className="muted">
            No message templates are configured yet. VNPF One doesn't currently have any system-generated emails
            with editable wording — this page will list them here once one exists.
          </p>
        </div>
      ) : (
        <>
          <div className="toolbar">
            <select value={selectedKey} onChange={(e) => setSelectedKey(e.target.value)}>
              {TEMPLATES.map((t) => (
                <option key={t.key} value={t.key}>
                  {t.title}
                </option>
              ))}
            </select>
          </div>

          {selected && <TemplateSection key={selected.key} templateKey={selected.key} title={selected.title} />}
        </>
      )}
    </>
  );
}
